import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { Group } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputText,
    ValidatedText,
    InputNullableDateTime,
    DateComp,
    InputSelect,
    DataSourceLookup,
    InputNullableInt,
    ValidatedInt,
    InputInt,
    InputBool,
    DisplayBool,
    InputDateTime,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useEventCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class GroupGuiDef extends GuiDef<Group> {
    constructor() {
        super('Group');

        const EventCommon = storeToRefs(useEventCommonStore());
        const RacesSource = computed(() => {
            return EventCommon.RacesAsSelectLine.value;
        });

        const NameValidator = new ValidatorDef({ Min: 1, Max: 99999999, Required: true, Type: undefined });
        const Name = new FieldDef<Group>();
        Name.Name = 'Name';
        Name.FieldName = 'Name';
        Name.DefinedFor = 'Both';
        Name.TranslationKey = 'group.name';
        Name.Editable = true;
        Name.Filterable = true;
        Name.Sortable = true;
        Name.CssClasses = '';
        Name.value = (group) => group.Name;
        Name.setValue = (group, value) => (group.Name = value);
        Name.DetailEditor = markRaw(InputText);
        Name.DetailEditorProps = {};
        Name.CellEditor = markRaw(InputText);
        Name.CellEditorProps = {};
        Name.CellRenderer = markRaw(ValidatedText);
        Name.CellRendererProps = {};
        Name.Validator = new ValidatorDef({ Min: 1, Max: 99999999, Required: true, Type: undefined });
        Name.SortDirection = undefined;
        Name.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(NameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Name.MinWidth = undefined;
        Name.MaxWidth = undefined;
        Name.Tooltip = null;
        this.Fields.push(Name);

        const StartTimeValidator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        const StartTime = new FieldDef<Group>();
        StartTime.Name = 'StartTime';
        StartTime.FieldName = 'StartTime';
        StartTime.DefinedFor = 'Both';
        StartTime.TranslationKey = 'group.starttime';
        StartTime.Editable = true;
        StartTime.Filterable = true;
        StartTime.Sortable = true;
        StartTime.CssClasses = '';
        StartTime.value = (group) => group.StartTime;
        StartTime.setValue = (group, value) => (group.StartTime = value);
        StartTime.DetailEditor = markRaw(InputNullableDateTime);
        StartTime.DetailEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartTime.CellEditor = markRaw(InputNullableDateTime);
        StartTime.CellEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartTime.CellRenderer = markRaw(DateComp);
        StartTime.CellRendererProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartTime.Validator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        StartTime.SortDirection = undefined;
        StartTime.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(StartTimeValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(StartTimeValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        StartTime.MinWidth = 180;
        StartTime.MaxWidth = undefined;
        StartTime.Tooltip = null;
        this.Fields.push(StartTime);

        const RaceIDValidator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        const RaceID = new FieldDef<Group>();
        RaceID.Name = 'RaceID';
        RaceID.FieldName = 'RaceID';
        RaceID.DefinedFor = 'Both';
        RaceID.TranslationKey = 'group.raceid';
        RaceID.Editable = true;
        RaceID.Filterable = false;
        RaceID.Sortable = true;
        RaceID.CssClasses = '';
        RaceID.value = (group) => group.RaceID;
        RaceID.setValue = (group, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            group.RaceID = value;
        };
        RaceID.DetailEditor = markRaw(InputSelect);
        RaceID.DetailEditorProps = { datasource: { mapsource: RacesSource } };
        RaceID.CellEditor = markRaw(InputSelect);
        RaceID.CellEditorProps = { datasource: { mapsource: RacesSource } };
        // @ts-expect-error @ts-ignore
        RaceID.CellRenderer = markRaw(DataSourceLookup);
        RaceID.CellRendererProps = { datasource: { mapsource: RacesSource } };
        RaceID.ExportValueFormatter = (obj: Group) => {
            return RacesSource.value.get(obj.RaceID)?.text;
        };
        RaceID.Validator = new ValidatorDef({ Min: null, Max: null, Required: true, Type: undefined });
        RaceID.SortDirection = undefined;
        RaceID.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(RaceIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(RaceIDValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        RaceID.MinWidth = undefined;
        RaceID.MaxWidth = undefined;
        RaceID.Tooltip = null;
        this.Fields.push(RaceID);

        const BibFrom = new FieldDef<Group>();
        BibFrom.Group = new GroupDef('group.bibfromto', '', false);
        BibFrom.Name = 'BibFrom';
        BibFrom.FieldName = 'BibFrom';
        BibFrom.DefinedFor = 'Both';
        BibFrom.TranslationKey = 'group.bibfrom';
        BibFrom.Editable = true;
        BibFrom.Filterable = false;
        BibFrom.Sortable = true;
        BibFrom.CssClasses = '';
        BibFrom.value = (group) => group.BibFrom;
        BibFrom.setValue = (group, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            group.BibFrom = value;
        };
        BibFrom.DetailEditor = markRaw(InputNullableInt);
        BibFrom.DetailEditorProps = {};
        BibFrom.CellEditor = markRaw(InputNullableInt);
        BibFrom.CellEditorProps = {};
        BibFrom.CellRenderer = markRaw(ValidatedInt);
        BibFrom.CellRendererProps = {};
        BibFrom.Validator = null;
        BibFrom.SortDirection = undefined;
        BibFrom.cellClassRules = undefined;
        BibFrom.MinWidth = undefined;
        BibFrom.MaxWidth = undefined;
        BibFrom.Tooltip = null;
        this.Fields.push(BibFrom);

        const BibTo = new FieldDef<Group>();
        BibTo.Group = new GroupDef('group.bibfromto', '', false);
        BibTo.Name = 'BibTo';
        BibTo.FieldName = 'BibTo';
        BibTo.DefinedFor = 'Both';
        BibTo.TranslationKey = 'group.bibto';
        BibTo.Editable = true;
        BibTo.Filterable = false;
        BibTo.Sortable = true;
        BibTo.CssClasses = '';
        BibTo.value = (group) => group.BibTo;
        BibTo.setValue = (group, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            group.BibTo = value;
        };
        BibTo.DetailEditor = markRaw(InputNullableInt);
        BibTo.DetailEditorProps = {};
        BibTo.CellEditor = markRaw(InputNullableInt);
        BibTo.CellEditorProps = {};
        BibTo.CellRenderer = markRaw(ValidatedInt);
        BibTo.CellRendererProps = {};
        BibTo.Validator = null;
        BibTo.SortDirection = undefined;
        BibTo.cellClassRules = undefined;
        BibTo.MinWidth = undefined;
        BibTo.MaxWidth = undefined;
        BibTo.Tooltip = null;
        this.Fields.push(BibTo);

        const WaveStart = new FieldDef<Group>();
        WaveStart.Name = 'WaveStart';
        WaveStart.FieldName = 'WaveStart';
        WaveStart.DefinedFor = 'Both';
        WaveStart.TranslationKey = 'group.wavestart';
        WaveStart.Editable = true;
        WaveStart.Filterable = true;
        WaveStart.Sortable = true;
        WaveStart.CssClasses = '';
        WaveStart.value = (group) => group.WaveStart;
        WaveStart.setValue = (group, value) => (group.WaveStart = value);
        WaveStart.DetailEditor = markRaw(InputNullableDateTime);
        WaveStart.DetailEditorProps = { format: 'DATETIME' as DateType };
        WaveStart.CellEditor = markRaw(InputNullableDateTime);
        WaveStart.CellEditorProps = { format: 'DATETIME' as DateType };
        WaveStart.CellRenderer = markRaw(DateComp);
        WaveStart.CellRendererProps = { format: 'DATETIME' as DateType };
        WaveStart.Validator = null;
        WaveStart.SortDirection = undefined;
        WaveStart.cellClassRules = undefined;
        WaveStart.MinWidth = 180;
        WaveStart.MaxWidth = undefined;
        WaveStart.Tooltip = null;
        this.Fields.push(WaveStart);

        const Sort = new FieldDef<Group>();
        Sort.Name = 'Sort';
        Sort.FieldName = 'Sort';
        Sort.DefinedFor = 'Both';
        Sort.TranslationKey = 'group.sort';
        Sort.Editable = true;
        Sort.Filterable = false;
        Sort.Sortable = true;
        Sort.CssClasses = '';
        Sort.value = (group) => group.Sort;
        Sort.setValue = (group, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            group.Sort = value;
        };
        Sort.DetailEditor = markRaw(InputInt);
        Sort.DetailEditorProps = {};
        Sort.CellEditor = markRaw(InputInt);
        Sort.CellEditorProps = {};
        Sort.CellRenderer = markRaw(ValidatedInt);
        Sort.CellRendererProps = {};
        Sort.Validator = null;
        Sort.SortDirection = undefined;
        Sort.cellClassRules = undefined;
        Sort.MinWidth = undefined;
        Sort.MaxWidth = undefined;
        Sort.Tooltip = null;
        this.Fields.push(Sort);

        const NumberOfLaps = new FieldDef<Group>();
        NumberOfLaps.Name = 'NumberOfLaps';
        NumberOfLaps.FieldName = 'NumberOfLaps';
        NumberOfLaps.DefinedFor = 'Both';
        NumberOfLaps.TranslationKey = 'group.numberoflaps';
        NumberOfLaps.Editable = true;
        NumberOfLaps.Filterable = false;
        NumberOfLaps.Sortable = true;
        NumberOfLaps.CssClasses = '';
        NumberOfLaps.value = (group) => group.NumberOfLaps;
        NumberOfLaps.setValue = (group, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            group.NumberOfLaps = value;
        };
        NumberOfLaps.DetailEditor = markRaw(InputNullableInt);
        NumberOfLaps.DetailEditorProps = {};
        NumberOfLaps.CellEditor = markRaw(InputNullableInt);
        NumberOfLaps.CellEditorProps = {};
        NumberOfLaps.CellRenderer = markRaw(ValidatedInt);
        NumberOfLaps.CellRendererProps = {};
        NumberOfLaps.Validator = null;
        NumberOfLaps.SortDirection = undefined;
        NumberOfLaps.cellClassRules = undefined;
        NumberOfLaps.MinWidth = undefined;
        NumberOfLaps.MaxWidth = undefined;
        NumberOfLaps.Tooltip = null;
        this.Fields.push(NumberOfLaps);

        const GrossTime = new FieldDef<Group>();
        GrossTime.Name = 'GrossTime';
        GrossTime.FieldName = 'GrossTime';
        GrossTime.DefinedFor = 'Both';
        GrossTime.TranslationKey = 'group.grosstime';
        GrossTime.Editable = true;
        GrossTime.Filterable = false;
        GrossTime.Sortable = true;
        GrossTime.CssClasses = '';
        GrossTime.value = (group) => group.GrossTime;
        GrossTime.setValue = (group, value) => (group.GrossTime = value);
        GrossTime.DetailEditor = markRaw(InputBool);
        GrossTime.DetailEditorProps = { label: 'group.grosstime' };
        GrossTime.CellEditor = markRaw(InputBool);
        GrossTime.CellEditorProps = {};
        GrossTime.CellRenderer = markRaw(DisplayBool);
        GrossTime.CellRendererProps = {};
        GrossTime.Validator = null;
        GrossTime.SortDirection = undefined;
        GrossTime.cellClassRules = undefined;
        GrossTime.MinWidth = undefined;
        GrossTime.MaxWidth = undefined;
        GrossTime.Tooltip = null;
        this.Fields.push(GrossTime);

        const TimeBeforeRoundCount = new FieldDef<Group>();
        TimeBeforeRoundCount.Name = 'TimeBeforeRoundCount';
        TimeBeforeRoundCount.FieldName = 'TimeBeforeRoundCount';
        TimeBeforeRoundCount.DefinedFor = 'Both';
        TimeBeforeRoundCount.TranslationKey = 'group.timebeforeroundcount';
        TimeBeforeRoundCount.Editable = true;
        TimeBeforeRoundCount.Filterable = true;
        TimeBeforeRoundCount.Sortable = true;
        TimeBeforeRoundCount.CssClasses = '';
        TimeBeforeRoundCount.value = (group) => group.TimeBeforeRoundCount;
        TimeBeforeRoundCount.setValue = (group, value) => (group.TimeBeforeRoundCount = value);
        TimeBeforeRoundCount.DetailEditor = markRaw(InputDateTime);
        TimeBeforeRoundCount.DetailEditorProps = { format: 'TIME' as DateType };
        TimeBeforeRoundCount.CellEditor = markRaw(InputDateTime);
        TimeBeforeRoundCount.CellEditorProps = { format: 'TIME' as DateType };
        TimeBeforeRoundCount.CellRenderer = markRaw(DateComp);
        TimeBeforeRoundCount.CellRendererProps = { format: 'TIME' as DateType };
        TimeBeforeRoundCount.Validator = null;
        TimeBeforeRoundCount.SortDirection = undefined;
        TimeBeforeRoundCount.cellClassRules = undefined;
        TimeBeforeRoundCount.MinWidth = 100;
        TimeBeforeRoundCount.MaxWidth = undefined;
        TimeBeforeRoundCount.Tooltip = null;
        this.Fields.push(TimeBeforeRoundCount);

        const HideFromLive = new FieldDef<Group>();
        HideFromLive.Name = 'HideFromLive';
        HideFromLive.FieldName = 'HideFromLive';
        HideFromLive.DefinedFor = 'Both';
        HideFromLive.TranslationKey = 'group.hidefromlive';
        HideFromLive.Editable = true;
        HideFromLive.Filterable = false;
        HideFromLive.Sortable = true;
        HideFromLive.CssClasses = '';
        HideFromLive.value = (group) => group.HideFromLive;
        HideFromLive.setValue = (group, value) => (group.HideFromLive = value);
        HideFromLive.DetailEditor = markRaw(InputBool);
        HideFromLive.DetailEditorProps = { label: 'group.hidefromlive' };
        HideFromLive.CellEditor = markRaw(InputBool);
        HideFromLive.CellEditorProps = {};
        HideFromLive.CellRenderer = markRaw(DisplayBool);
        HideFromLive.CellRendererProps = {};
        HideFromLive.Validator = null;
        HideFromLive.SortDirection = undefined;
        HideFromLive.cellClassRules = undefined;
        HideFromLive.MinWidth = undefined;
        HideFromLive.MaxWidth = undefined;
        HideFromLive.Tooltip = null;
        this.Fields.push(HideFromLive);

        const MaxContestants = new FieldDef<Group>();
        MaxContestants.Name = 'MaxContestants';
        MaxContestants.FieldName = 'MaxContestants';
        MaxContestants.DefinedFor = 'Both';
        MaxContestants.TranslationKey = 'group.maxcontestants';
        MaxContestants.Editable = true;
        MaxContestants.Filterable = false;
        MaxContestants.Sortable = true;
        MaxContestants.CssClasses = '';
        MaxContestants.value = (group) => group.MaxContestants;
        MaxContestants.setValue = (group, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            group.MaxContestants = value;
        };
        MaxContestants.DetailEditor = markRaw(InputNullableInt);
        MaxContestants.DetailEditorProps = {};
        MaxContestants.CellEditor = markRaw(InputNullableInt);
        MaxContestants.CellEditorProps = {};
        MaxContestants.CellRenderer = markRaw(ValidatedInt);
        MaxContestants.CellRendererProps = {};
        MaxContestants.Validator = null;
        MaxContestants.SortDirection = undefined;
        MaxContestants.cellClassRules = undefined;
        MaxContestants.MinWidth = undefined;
        MaxContestants.MaxWidth = undefined;
        MaxContestants.Tooltip = null;
        this.Fields.push(MaxContestants);
    }

    public async LoadDependencies() {
        await useEventCommonStore().EnsureLoaded(['Races']);
    }
}
